import { useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

export interface RequisitoSenha {
  id: string;
  texto: string;
  valido: boolean;
}

interface RetornoRequisitosSenha {
  requisitosSenha: RequisitoSenha[];
  senhaAtendeRequisitos: boolean;
  forcaSenha: number;
  classeCorBarraForca: string;
  classeCorTextoForca: string;
}

const useRequisitosSenha = (senhaInformada: string): RetornoRequisitosSenha => {
  // Comentario (function-level): Centraliza o calculo dos requisitos e da forca da senha
  // para manter os fluxos de reset e troca logada com a mesma politica visual e funcional.
  const { t } = useLanguage();

  const requisitosSenha = useMemo<RequisitoSenha[]>(() => ([
    { id: 'length', texto: t('auth.passwordMinLength'), valido: senhaInformada.length >= 8 },
    { id: 'upper', texto: t('auth.passwordUpperCase'), valido: /[A-Z]/.test(senhaInformada) },
    { id: 'lower', texto: t('auth.passwordLowerCase'), valido: /[a-z]/.test(senhaInformada) },
    { id: 'number', texto: t('auth.passwordNumber'), valido: /[0-9]/.test(senhaInformada) }
  ]), [senhaInformada, t]);

  const senhaAtendeRequisitos = useMemo(
    () => requisitosSenha.every((requisitoSenha) => requisitoSenha.valido),
    [requisitosSenha]
  );

  const forcaSenha = useMemo(() => {
    const quantidadeRequisitosValidos = requisitosSenha.filter((requisitoSenha) => requisitoSenha.valido).length;
    return (quantidadeRequisitosValidos / requisitosSenha.length) * 100;
  }, [requisitosSenha]);

  const classeCorTextoForca = useMemo(() => {
    if (forcaSenha < 25) return 'text-red-500';
    if (forcaSenha < 50) return 'text-amber-500';
    if (forcaSenha < 75) return 'text-sky-500';
    return 'text-green-500';
  }, [forcaSenha]);

  const classeCorBarraForca = useMemo(() => {
    if (forcaSenha < 25) return 'bg-red-500';
    if (forcaSenha < 50) return 'bg-amber-500';
    if (forcaSenha < 75) return 'bg-sky-500';
    return 'bg-green-500';
  }, [forcaSenha]);

  return {
    requisitosSenha,
    senhaAtendeRequisitos,
    forcaSenha,
    classeCorBarraForca,
    classeCorTextoForca
  };
};

export default useRequisitosSenha;
